import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpRDN-RN (Receipt Note / Virtual Warehouse).
// PRESENTATIONAL dialog: HTML đến qua props (parent fetch via
// useLazyGetRnPrintPreviewQuery). Mirror DoPrintDialog: iframe srcDoc preview +
// nút Print → printAndUploadReport.

interface PrintDialogProps {
  open: boolean
  /** Tiêu đề dialog (vd "Print Preview - RN (RDN-RN-001)"). */
  title: string
  /** HTML preview (đã do parent fetch + buildPreviewHtml). */
  html: string
  /** Đang fetch HTML (overlay spinner). */
  loading: boolean
  canEdit: boolean
  onClose: () => void
}

export default function RnPrintDialog({
  open,
  title,
  html,
  loading,
  canEdit,
  onClose,
}: PrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'RN Print Preview'
  // Report ~198mm (gần full A4) > vùng content 190mm của sheet (padding mặc định
  // 9mm 10mm) → lòi dải trắng lề phải. Giảm padding ngang 5mm (content 200mm).
  const previewHtml = html ? buildPrintPreviewHtml(html, { styleId: 'rn-print-preview-overrides', pagePadding: '9mm 5mm' }) : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title || 'Receipt Note'}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            RN
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  const idPart = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  const printTitle = `${sanitizePdfFileNamePart(idPart || 'receipt_note')}_RN.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpRDN-RN',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
